/**
 * Tipo central do "draft" do AgentEditor. Concentra todos os campos
 * que as 7 abas editam, mais campos derivados como `kbIds`, `mcpItems`,
 * `visibility`. O componente AgentEditorPage mantem esse objeto em
 * `useState` e cada Tab recebe um slice + setters.
 */

import type {
  AgentDetail,
  AgentExternalAuthType,
  AgentExternalConfig,
  AgentExternalConfigPut,
  AgentFallbackBehavior,
  AgentMcpPutItem,
  AgentOttoConfig,
  AgentReportTheme,
  AgentResponseTone,
  AgentType,
  AgentVisibility,
  ExternalHealthStatus,
  ReasoningEffort,
} from "@/api/types";

/**
 * Slice da config Otto editada na aba "Otto". `null` no draft = config
 * ainda nao carregada/criada. A aba materializa o objeto quando o usuario
 * interage.
 */
export interface OttoConfigDraft {
  enabled: boolean;
  funnel_prompt_text: string;
  funnel_prompt_label: string;
  funnel_steps: string[];
  tags_prompt_text: string;
  tags_prompt_label: string;
  tag_options: string[];
  followup_prompt_text: string;
  followup_prompt_label: string;
}

export function emptyOttoConfig(): OttoConfigDraft {
  return {
    enabled: false,
    funnel_prompt_text: "",
    funnel_prompt_label: "production",
    funnel_steps: [],
    tags_prompt_text: "",
    tags_prompt_label: "production",
    tag_options: [],
    followup_prompt_text: "",
    followup_prompt_label: "production",
  };
}

export function ottoConfigFromApi(cfg: AgentOttoConfig): OttoConfigDraft {
  return {
    enabled: cfg.enabled,
    funnel_prompt_text: cfg.funnel_prompt_text,
    funnel_prompt_label: cfg.funnel_prompt_label,
    funnel_steps: [...cfg.funnel_steps],
    tags_prompt_text: cfg.tags_prompt_text,
    tags_prompt_label: cfg.tags_prompt_label,
    tag_options: [...cfg.tag_options],
    followup_prompt_text: cfg.followup_prompt_text,
    followup_prompt_label: cfg.followup_prompt_label,
  };
}

/**
 * Slice da conexao externa (aba "Conexao externa", so agente externo).
 * `null` no draft = ainda nao carregada/criada. Segredos sao write-only:
 * o draft guarda apenas o que o usuario DIGITOU nesta sessao -- string
 * vazia significa "manter o atual" (vira `null` no PUT); `has_secret` /
 * `has_langfuse_secret` indicam que ja existe valor salvo no backend.
 */
export interface ExternalConfigDraft {
  base_url: string;
  auth_type: AgentExternalAuthType;
  /** Novo segredo digitado nesta sessao ("" = manter o salvo). */
  secret: string;
  /** Marcar true para limpar o segredo salvo no proximo save. */
  clear_secret: boolean;
  timeout_seconds: number;
  langfuse_host: string;
  langfuse_public_key: string;
  langfuse_secret: string;
  clear_langfuse_secret: boolean;
  // Read-only (vem do GET; nao participam do PUT)
  has_secret: boolean;
  has_langfuse_secret: boolean;
  health_status: ExternalHealthStatus | null;
  last_health_check_at: string | null;
}

export function emptyExternalConfig(): ExternalConfigDraft {
  return {
    base_url: "",
    auth_type: "none",
    secret: "",
    clear_secret: false,
    timeout_seconds: 120,
    langfuse_host: "",
    langfuse_public_key: "",
    langfuse_secret: "",
    clear_langfuse_secret: false,
    has_secret: false,
    has_langfuse_secret: false,
    health_status: null,
    last_health_check_at: null,
  };
}

export function externalConfigFromApi(
  cfg: AgentExternalConfig,
): ExternalConfigDraft {
  return {
    base_url: cfg.base_url,
    auth_type: cfg.auth_type,
    secret: "",
    clear_secret: false,
    timeout_seconds: cfg.timeout_seconds,
    langfuse_host: cfg.langfuse_host ?? "",
    langfuse_public_key: cfg.langfuse_public_key ?? "",
    langfuse_secret: "",
    clear_langfuse_secret: false,
    has_secret: cfg.has_secret,
    has_langfuse_secret: cfg.has_langfuse_secret,
    health_status: cfg.health_status,
    last_health_check_at: cfg.last_health_check_at,
  };
}

/** Converte o draft -> corpo do PUT (semantica write-only dos segredos). */
export function externalConfigToPut(
  d: ExternalConfigDraft,
): AgentExternalConfigPut {
  const secretValue = (typed: string, clear: boolean): string | null => {
    if (clear) return "";
    const v = typed.trim();
    return v ? v : null; // null = mantem o salvo
  };
  const orNull = (s: string) => {
    const v = s.trim();
    return v ? v : null;
  };
  return {
    base_url: d.base_url.trim(),
    auth_type: d.auth_type,
    secret: secretValue(d.secret, d.clear_secret),
    timeout_seconds: d.timeout_seconds,
    langfuse_host: orNull(d.langfuse_host),
    langfuse_public_key: orNull(d.langfuse_public_key),
    langfuse_secret: secretValue(d.langfuse_secret, d.clear_langfuse_secret),
  };
}

/**
 * Slice do tema de relatorio editado na aba "Relatorios". `null` no draft
 * = ainda nao carregado/criado. Strings vazias viram `null` no PUT.
 */
export interface ReportThemeDraft {
  enabled: boolean;
  primary_color: string;
  accent_color: string;
  text_color: string;
  background_color: string;
  heading_font: string;
  body_font: string;
  chart_palette: string[];
  logo_document_id: string;
  header_html: string;
  footer_html: string;
  template_document_id: string;
}

export function emptyReportTheme(): ReportThemeDraft {
  return {
    enabled: false,
    primary_color: "",
    accent_color: "",
    text_color: "",
    background_color: "",
    heading_font: "",
    body_font: "",
    chart_palette: [],
    logo_document_id: "",
    header_html: "",
    footer_html: "",
    template_document_id: "",
  };
}

export function reportThemeFromApi(cfg: AgentReportTheme): ReportThemeDraft {
  return {
    enabled: cfg.enabled,
    primary_color: cfg.primary_color ?? "",
    accent_color: cfg.accent_color ?? "",
    text_color: cfg.text_color ?? "",
    background_color: cfg.background_color ?? "",
    heading_font: cfg.heading_font ?? "",
    body_font: cfg.body_font ?? "",
    chart_palette: [...cfg.chart_palette],
    logo_document_id: cfg.logo_document_id ?? "",
    header_html: cfg.header_html ?? "",
    footer_html: cfg.footer_html ?? "",
    template_document_id: cfg.template_document_id ?? "",
  };
}

/** Converte o draft -> corpo do PUT (strings vazias viram null). */
export function reportThemeToPut(d: ReportThemeDraft) {
  const orNull = (s: string) => {
    const v = s.trim();
    return v ? v : null;
  };
  return {
    enabled: d.enabled,
    primary_color: orNull(d.primary_color),
    accent_color: orNull(d.accent_color),
    text_color: orNull(d.text_color),
    background_color: orNull(d.background_color),
    heading_font: orNull(d.heading_font),
    body_font: orNull(d.body_font),
    chart_palette: d.chart_palette,
    logo_document_id: orNull(d.logo_document_id),
    header_html: orNull(d.header_html),
    footer_html: orNull(d.footer_html),
    template_document_id: orNull(d.template_document_id),
  };
}

export interface AgentDraft {
  /** Tipo do agente -- escolhido na criacao, imutavel depois. */
  agent_type: AgentType;
  // Aba Identidade
  name: string;
  slug: string;
  description: string;
  icon: string | null;
  owner_team_id: string;
  // Aba Comportamento
  system_prompt: string;
  response_tone: AgentResponseTone | null;
  response_language: string;
  fallback_behavior: AgentFallbackBehavior | null;
  memory_enabled: boolean;
  cite_sources: boolean;
  auto_escalate_low_confidence: boolean;
  // Aba Modelo
  llm_model_id: string;
  /** null = usa o default do modelo (que pode ser null = sem reasoning). */
  reasoning_effort: ReasoningEffort | null;
  pii_filter_enabled: boolean;
  /** R6: controla as tools de relatorio (save_artifact + build_report +
   *  render_chart). Default ``true`` preserva comportamento historico. */
  reports_enabled: boolean;
  /** R6: ativa web search nativo do provider. Requer LlmModel
   *  ``supports_web_search=true``. Default ``false`` (opt-in). */
  web_search_enabled: boolean;
  // Aba Visibilidade
  visibility: AgentVisibility;
  visibility_team_ids: string[];
  visibility_user_ids: string[];
  // Aba Publicacao
  requires_install_approval: boolean;
  approval_responsible_user_id: string | null;
  // Aba Conhecimento
  kb_ids: string[];
  // Aba Ferramentas
  mcp_items: AgentMcpPutItem[];
  /** Skills linkadas (liga/desliga). Hidratadas via useAgentSkills. */
  skill_ids: string[];
  // Aba Otto (null = ainda nao carregada/criada)
  otto_config: OttoConfigDraft | null;
  // Aba Relatorios / tema (null = ainda nao carregado/criado)
  report_theme: ReportThemeDraft | null;
  // Aba Conexao externa (so agent_type=external; null = nao carregada)
  external_config: ExternalConfigDraft | null;
}

export function emptyDraft(): AgentDraft {
  return {
    agent_type: "native",
    name: "",
    slug: "",
    description: "",
    icon: null,
    owner_team_id: "",
    system_prompt: "",
    response_tone: null,
    response_language: "pt-BR",
    fallback_behavior: null,
    memory_enabled: true,
    cite_sources: true,
    auto_escalate_low_confidence: false,
    llm_model_id: "",
    reasoning_effort: null,
    pii_filter_enabled: false,
    reports_enabled: true,
    web_search_enabled: false,
    visibility: "private",
    visibility_team_ids: [],
    visibility_user_ids: [],
    requires_install_approval: false,
    approval_responsible_user_id: null,
    kb_ids: [],
    mcp_items: [],
    skill_ids: [],
    otto_config: null,
    report_theme: null,
    external_config: null,
  };
}

export function draftFromAgent(agent: AgentDetail): AgentDraft {
  return {
    agent_type: agent.agent_type ?? "native",
    name: agent.name,
    slug: agent.slug,
    description: agent.description,
    icon: agent.icon,
    owner_team_id: agent.owner_team_id,
    system_prompt: agent.system_prompt,
    response_tone: agent.response_tone,
    response_language: agent.response_language,
    fallback_behavior: agent.fallback_behavior,
    memory_enabled: agent.memory_enabled,
    cite_sources: agent.cite_sources,
    auto_escalate_low_confidence: agent.auto_escalate_low_confidence,
    llm_model_id: agent.llm_model_id ?? "",
    reasoning_effort: agent.reasoning_effort,
    pii_filter_enabled: agent.pii_filter_enabled,
    reports_enabled: agent.reports_enabled,
    web_search_enabled: agent.web_search_enabled,
    visibility: agent.visibility,
    visibility_team_ids: [],
    visibility_user_ids: [],
    requires_install_approval: agent.requires_install_approval,
    approval_responsible_user_id: agent.approval_responsible_user_id,
    kb_ids: [],
    mcp_items: [],
    skill_ids: [],
    otto_config: null,
    report_theme: null,
    external_config: null,
  };
}

export type TabName =
  | "identity"
  | "behavior"
  | "model"
  | "knowledge"
  | "tools"
  | "visibility"
  | "publication"
  | "otto"
  | "report_theme"
  | "external"
  | "testing"
  | "ratings";

export const TAB_ORDER: TabName[] = [
  "identity",
  "behavior",
  "model",
  "knowledge",
  "tools",
  "visibility",
  "publication",
  "otto",
  "report_theme",
];

/** Abas do agente EXTERNO: sem comportamento/modelo/conhecimento/
 *  ferramentas/tema (o bot remoto e quem responde). */
export const EXTERNAL_TAB_ORDER: TabName[] = [
  "identity",
  "external",
  "visibility",
  "publication",
  "otto",
];

export function tabsForType(agentType: AgentType): TabName[] {
  return agentType === "external" ? EXTERNAL_TAB_ORDER : TAB_ORDER;
}
